/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Modal } from 'bootstrap'
import clsx from 'clsx'
import { useDebounce } from '../../../../../../_metronic/helpers'
import { getJobsApplicant } from '../../_requests'

interface Applicant {
  id: number
  job_id: number
  support_worker_id: number
  applied_at: string
  status: string
  first_name: string
  last_name: string
  email: string
  phone: string
  profile_picture_url: string | null
  resume_file_url: string | null
  distance_km: number
}

interface Pagination {
  page: number
  items_per_page: number
  total: number
  total_pages: number
  links?: Array<{ label: string; active: boolean; url: string | null; page: number | null }>
}

interface ApplicantsModalProps {
  jobId: string
  jobTitle: string
  isOpen: boolean
  onClose: () => void
}

const ApplicantsModal: React.FC<ApplicantsModalProps> = ({ jobId, jobTitle, isOpen, onClose }) => {
  const [applicants, setApplicants] = useState<Applicant[]>([])
  const [loading, setLoading] = useState(false)
  const modalRef = useRef<Modal | null>(null)
  const onCloseRef = useRef(onClose)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
        items_per_page: 15,
    total: 0,
    total_pages: 1,
    links: [],
  })

  const hasFetched = useRef(false)

  const ensureBodyScrollRestored = useCallback(() => {
    document.body.classList.remove('modal-open')
    const existingBackdrop = document.querySelector('.modal-backdrop')
    if (existingBackdrop && existingBackdrop.parentElement) {
      existingBackdrop.parentElement.removeChild(existingBackdrop)
    }
  }, [])

  const itemsPerPage = 15
  const debouncedSearchTerm = useDebounce(searchTerm, 150)

  useEffect(() => {
    if (!isOpen) {
      hasFetched.current = false
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && jobId) {
      setCurrentPage(1)
      fetchApplicants(1, debouncedSearchTerm)
    }
  }, [debouncedSearchTerm, isOpen, jobId])

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    const modalElement = document.getElementById('applicantsModal')
    if (!modalElement) return

    const bsModal = new Modal(modalElement, {
      backdrop: 'static',
      keyboard: false,
    })

    modalRef.current = bsModal

    const handleHidden = () => {
      ensureBodyScrollRestored()
      onCloseRef.current()
    }

    modalElement.addEventListener('hidden.bs.modal', handleHidden)

    return () => {
      modalElement.removeEventListener('hidden.bs.modal', handleHidden)
      bsModal.dispose()
      ensureBodyScrollRestored()
    }
  }, [ensureBodyScrollRestored])

  useEffect(() => {
    if (!modalRef.current) return

    if (isOpen) {
      modalRef.current.show()
    } else {
      modalRef.current.hide()
    }
  }, [isOpen])

  const fetchApplicants = useCallback(async (page: number = 1, search: string = '') => {
    setLoading(true)
    try {
      const fetchItemsPerPage = search ? 1000 : itemsPerPage
      let queryString = `page=${page}&items_per_page=${fetchItemsPerPage}`
      
      if (search) {
        queryString += `&search=${encodeURIComponent(search)}`
      }

      // Convert jobId string to number for the API call
      const jobIdAsNumber = Number(jobId)
      const response = await getJobsApplicant(jobIdAsNumber, queryString)
      
      if (response && response.data) {
        let applicantsData = response.data || []
        const paginationData = response.payload?.pagination || {
          page: 1,
          items_per_page: itemsPerPage,
          total: applicantsData.length,
          total_pages: 1,
          links: []
        }

        // Client-side filtering if search term exists
        if (search) {
          const lower = search.toLowerCase()
          const filtered = applicantsData.filter((applicant: any) => {
            const firstName = applicant.first_name || ''
            const lastName = applicant.last_name || ''
            const email = applicant.email || ''
            const phone = applicant.phone || ''
            const combined = `${firstName} ${lastName} ${email} ${phone}`.toLowerCase()
            return combined.includes(lower)
          })

          const totalFiltered = filtered.length
          const pageStart = (page - 1) * itemsPerPage
          const paginated = filtered.slice(pageStart, pageStart + itemsPerPage)
          const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage))

          setApplicants(paginated as Applicant[])
          setPagination({
            page,
            items_per_page: itemsPerPage,
            total: totalFiltered,
            total_pages: totalPages,
            links: paginationData.links || [],
          })
        } else {
          setApplicants(applicantsData as Applicant[])
          setPagination({
            page: paginationData.page || 1,
            items_per_page: paginationData.items_per_page || itemsPerPage,
            total: paginationData.total ?? applicantsData.length,
            total_pages: paginationData.total_pages || 1,
            links: paginationData.links || [],
          })
        }
      }
    } catch (error) {
      console.error('Failed to fetch applicants:', error)
      setApplicants([])
    } finally {
      setLoading(false)
    }
  }, [jobId])

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.total_pages && page !== currentPage) {
      setCurrentPage(page)
      fetchApplicants(page, debouncedSearchTerm)
    }
  }

  const renderPagination = () => {
    const pages: React.ReactNode[] = []
    const maxVisible = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let endPage = Math.min(pagination.total_pages, startPage + maxVisible - 1)

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1)
    }

    // First button
    pages.push(
      <li
        key="first"
        className={clsx('page-item', {
          disabled: loading || currentPage === 1,
        })}
      >
        <button
          type="button"
          className="page-link"
          style={{ cursor: 'pointer' }}
          onClick={() => handlePageChange(1)}
          disabled={loading || currentPage === 1}
        >
          First
        </button>
      </li>
    )

    // Previous button
    if (currentPage > 1) {
      pages.push(
        <li key="prev" className="page-item">
          <button
            type="button"
            className="page-link"
            style={{ cursor: 'pointer' }}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Previous
          </button>
        </li>
      )
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={clsx('page-item', { active: i === currentPage })}>
          <button
            type="button"
            className="page-link"
            style={{ cursor: 'pointer' }}
            onClick={() => handlePageChange(i)}
            disabled={i === currentPage}
          >
            {i}
          </button>
        </li>
      )
    }

    // Next button
    if (currentPage < pagination.total_pages) {
      pages.push(
        <li key="next" className="page-item">
          <button
            type="button"
            className="page-link"
            style={{ cursor: 'pointer' }}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next
          </button>
        </li>
      )
    }

    // Last button
    pages.push(
      <li
        key="last"
        className={clsx('page-item', {
          disabled: loading || currentPage === pagination.total_pages,
        })}
      >
        <button
          type="button"
          className="page-link"
          style={{ cursor: 'pointer' }}
          onClick={() => handlePageChange(pagination.total_pages)}
          disabled={loading || currentPage === pagination.total_pages}
        >
          Last
        </button>
      </li>
    )

    return (
      <nav aria-label="Applicants pagination" className="mt-4">
        <ul className="pagination justify-content-end">{pages}</ul>
      </nav>
    )
  }

  return (
    <div
      className="modal fade"
      id="applicantsModal"
      tabIndex={-1}
      aria-labelledby="applicantsModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h5 className="modal-title" id="applicantsModalLabel">
              Total Applicants ({pagination.total}) for: <strong>{jobTitle}</strong>
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => modalRef.current?.hide()}
              aria-label="Close"
            ></button>
          </div>
          
          <div className="modal-body">
            {/* Search Bar */}
            <div className="mb-4">
              <div className="d-flex align-items-center position-relative">
                <i className="ki-duotone ki-magnifier fs-3 position-absolute ms-3">
                  <span className="path1"></span>
                  <span className="path2"></span>
                </i>
                <input
                  type="text"
                  className="form-control form-control-solid ps-13"
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '2.5rem' }}
                  autoComplete='off'
                />
              </div>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="text-center py-10">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mt-3">Loading applicants...</p>
              </div>
            ) : applicants.length === 0 ? (
              <div className="text-center py-10">
                <div className="text-muted fs-5">
                  <i className="ki-duotone ki-inbox fs-3x mb-3 d-block text-gray-300">
                    <span className="path1"></span>
                    <span className="path2"></span>
                  </i>
                  No applicants found.
                </div>
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-4">
                    <thead className="bg-light">
                      <tr className="text-start text-gray-700 fw-bolder fs-7 text-uppercase gs-0">
                        <th className="min-w-175px">Name</th>
                        <th className="min-w-200px">Email</th>
                        <th className="min-w-150px">Phone</th>
                        <th className="min-w-130px">Applied Date</th>
                        <th className="min-w-110px">Distance (km)</th>
                        {/* <th className="min-w-110px text-center">Status</th> */}
                        <th className="min-w-150px">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {applicants.map((applicant) => (
                        <tr key={applicant.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="symbol symbol-40px me-3">
                                <img
                                  src={applicant.profile_picture_url || '/media/avatars/placeholder.png'}
                                  alt={`${applicant.first_name} ${applicant.last_name}`}
                                  className="symbol-label rounded-pill"
                                />
                              </div>
                              <div className="d-flex flex-column">
                                <span className="text-gray-800 fw-bold">
                                  {applicant.first_name} {applicant.last_name}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="text-gray-800">{applicant.email || '-'}</span>
                          </td>
                          <td>
                            <span className="text-gray-800">{applicant.phone || '-'}</span>
                          </td>
                          <td>
                            <span className="text-gray-800">
                              {applicant.applied_at
                                ? new Date(applicant.applied_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })
                                : '-'}
                            </span>
                          </td>
                          <td>
                            <span className="text-gray-800">{applicant.distance_km} km</span>
                          </td>
                          {/* <td className="text-center">
                            <span
                              className={`badge fs-8 fw-bold ${
                                applicant.status === 'Pending'
                                  ? 'bg-warning'
                                  : applicant.status === 'Approved'
                                  ? 'bg-success'
                                  : applicant.status === 'Rejected'
                                  ? 'bg-danger'
                                  : 'bg-secondary'
                              }`}
                            >
                              {applicant.status}
                            </span>
                          </td> */}
                          <td>
                            {applicant.resume_file_url && (
                              <a
                                href={applicant.resume_file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-light-primary me-2"
                              >
                                View Resume
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Info */}
                <div className="row mt-4 align-items-center">
                  <div className="col-md-6">
                    <div className="text-gray-600 fs-12">
                      Showing <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> to{' '}
                      <strong>{Math.min(currentPage * itemsPerPage, pagination.total)}</strong> of{' '}
                      <strong>{pagination.total}</strong> applicants
                    </div>
                  </div>
                  <div className="col-md-6 text-end">
                    {renderPagination()}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer bg-light">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => modalRef.current?.hide()}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApplicantsModal